import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import WebIcon from '@mui/icons-material/Web';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import {
  ADMIN_UI_LABEL_DEFINITIONS,
  AdminUiLabelGroup,
  AdminUiLabelKey,
  DEFAULT_ADMIN_UI_LABELS,
  parseAdminUiLabels,
} from '../../constants/adminUiLabels';

interface AdminUiLabelsPanelProps {
  businessId: string;
  /** Raw value from GET /businesses/:id */
  adminUiLabelsRaw: unknown;
  onSaved?: () => void;
}

const GROUP_META: Record<AdminUiLabelGroup, { label: string; icon: React.ReactNode; hint: string }> = {
  web: {
    label: 'Web admin portal',
    icon: <WebIcon fontSize="small" sx={{ mr: 0.75, verticalAlign: 'middle' }} />,
    hint: 'These labels appear in the sidebar and top bar that business admins see on the web.',
  },
  mobile: {
    label: 'Mobile app',
    icon: <PhoneIphoneIcon fontSize="small" sx={{ mr: 0.75, verticalAlign: 'middle' }} />,
    hint: 'These labels appear in the tab bar and More screen of the iOS/Android app. Changes take effect the next time the app comes to the foreground (no reinstall needed).',
  },
  calendar: {
    label: 'Calendar',
    icon: <CalendarMonthIcon fontSize="small" sx={{ mr: 0.75, verticalAlign: 'middle' }} />,
    hint: 'These labels appear in calendar schedule filters and event type badges on the web admin calendar and the mobile Calendar tab.',
  },
};

const AdminUiLabelsPanel: React.FC<AdminUiLabelsPanelProps> = ({
  businessId,
  adminUiLabelsRaw,
  onSaved,
}) => {
  const baseline = useMemo(() => parseAdminUiLabels(adminUiLabelsRaw), [adminUiLabelsRaw]);
  const [draft, setDraft] = useState<Partial<Record<AdminUiLabelKey, string>>>(() => baseline);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setDraft(baseline);
  }, [baseline]);

  const handleChange = (key: AdminUiLabelKey, value: string) => {
    setDraft((prev) => {
      const next = { ...prev };
      const trimmed = value.trim();
      if (!trimmed || trimmed === DEFAULT_ADMIN_UI_LABELS[key]) {
        delete next[key];
      } else {
        next[key] = trimmed.slice(0, 120);
      }
      return next;
    });
    setSuccess(false);
    setError(null);
  };

  const handleResetAll = () => {
    setDraft({});
    setSuccess(false);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not signed in');

      await axios.put(
        `${API_BASE_URL}/businesses/${businessId}/admin-ui-labels`,
        { labels: draft },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      onSaved?.();
      window.setTimeout(() => setSuccess(false), 5000);
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : err instanceof Error
            ? err.message
            : 'Failed to save labels';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const groups: AdminUiLabelGroup[] = ['web', 'mobile', 'calendar'];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Admin UI labels
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customise navigation and section wording for this business. Leave a field blank (or type the
        default) to use the standard label. Changes are picked up automatically — web admins need to
        refresh; mobile users see changes next time the app comes to the foreground.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Labels saved successfully. Web admins: refresh the page. Mobile users: bring the app to
          the foreground.
        </Alert>
      )}

      {groups.map((group) => {
        const meta = GROUP_META[group];
        const defs = ADMIN_UI_LABEL_DEFINITIONS.filter((d) => d.group === group);
        return (
          <Box key={group} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {meta.icon}
                {meta.label}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {meta.hint}
            </Typography>
            <Grid container spacing={2}>
              {defs.map((def) => (
                <Grid item xs={12} sm={6} key={def.key}>
                  <Tooltip title={`Key: ${def.key}`} placement="top-start">
                    <TextField
                      fullWidth
                      size="small"
                      label={def.defaultLabel}
                      helperText={def.description}
                      placeholder={DEFAULT_ADMIN_UI_LABELS[def.key]}
                      value={draft[def.key] ?? ''}
                      onChange={(e) => handleChange(def.key, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 120 }}
                    />
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ mt: 3 }} />
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={22} /> : 'Save labels'}
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleResetAll} disabled={saving}>
          Clear all overrides (restore defaults)
        </Button>
      </Box>
    </Box>
  );
};

export default AdminUiLabelsPanel;
